import { Component, inject, signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import Keycloak from 'keycloak-js';
import { MessageService } from 'primeng/api';
import { ListboxClickEvent, Listbox } from 'primeng/listbox';
import { from, map } from 'rxjs';
import { EnvironmentService } from 'src/app/services/environment/environment.service';
import { RgsHttpErrorResponse } from 'src/app/shared/models/http-error.model';
import { RgsKeycloakProfile } from 'src/app/shared/models/rgs-keycloak-profile.model';
import { LicenseSwitchService } from 'src/app/shared/services/license-switch.service';
import { ArrayUtils } from 'src/app/shared/utils/array.utils';
import { ToastUtils } from 'src/app/shared/utils/toast.utils';
import { ProgressSpinner } from 'primeng/progressspinner';
import { UpperCasePipe } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'app-environment-selector',
  templateUrl: './environment-selector.component.html',
  imports: [ProgressSpinner, Listbox, UpperCasePipe, TranslatePipe],
})
export class EnvironmentSelectorComponent {
  private readonly keycloack = inject(Keycloak);
  private readonly environmentService = inject(EnvironmentService);
  private readonly router = inject(Router);
  private readonly licenseSwitch = inject(LicenseSwitchService);
  private readonly toastService = inject(MessageService);

  public licensesResource = rxResource({
    defaultValue: [],
    stream: () =>
      from(this.keycloack.loadUserProfile()).pipe(
        map((profile) => {
          const rgsProfile = profile as RgsKeycloakProfile;
          return ArrayUtils.ensureArray(rgsProfile?.attributes?.licentie)
            .sort((a, b) => a.localeCompare(b))
            .map((license) => ({ label: license, value: license }));
        }),
      ),
  });

  public loading = signal(false);

  public licenseClicked(event: ListboxClickEvent) {
    const license = event.option.value;

    this.loading.set(true);
    this.licenseSwitch.switchTo(license).subscribe({
      next: () => {
        const oldUrl = this.environmentService.oldUrl;
        if (oldUrl && oldUrl !== '' && oldUrl !== 'environment' && oldUrl !== 'environment-selector') {
          const baseUrl = window.location.origin;
          if (oldUrl.startsWith(baseUrl)) {
            const relativeUrl = oldUrl.substring(baseUrl.length);
            this.router.navigateByUrl(relativeUrl);
          } else {
            this.router.navigateByUrl(oldUrl);
          }
        } else {
          this.router.navigateByUrl('');
        }
      },
      error: (err: RgsHttpErrorResponse) => {
        ToastUtils.showError(this.toastService, null, err.error);
        this.loading.set(false);
      },
      complete: () => {
        this.loading.set(false);
      },
    });
  }

  public logout() {
    window.sessionStorage.removeItem('environment');
    window.sessionStorage.removeItem('tenant');

    this.loading.set(true);
    this.keycloack
      .logout()
      .then(() => {
        this.loading.set(false);
      })
      .catch(() => {
        this.loading.set(false);
      });
  }
}
